import useEcocashPayBooks from "api/PaymentsResource/useEcocashPayBooks";
import { ButtonLoader } from "components";
import { Form, Formik } from "formik";
import { activeCurrencies, activePaymentMethods } from "utils";
import { FiSmartphone, FiLock, FiAlertCircle, FiDollarSign } from "react-icons/fi";
import { FaGlobe } from "react-icons/fa";
import * as Yup from "yup";

const EcocashPaymentUsd = ({ grandTotal, books, setPaymentIsSuccessful, addressId, recipientUserId }) => {
	const initialValues = {
		street_address: "",
		city: "",
		province: "",
		country: "",
		postcode: "",
		payment_method: activePaymentMethods.ecocashUsd.name,
		payment_type: "Purchase",
		subtotal: 0,
		grand_charges: 0,
		grand_total: grandTotal,
		grand_total_zwl: 0,
		order_note: "",
		books: books,
		mobile_number: "",
		initiated_from: "cart",
		currency: activeCurrencies.USD.code,
		total_amount: grandTotal,
		address_id: addressId,
		...(recipientUserId && { recipient_user_id: recipientUserId }),
	};

	const validateCheckout = Yup.object().shape({
		mobile_number: Yup.string()
			.typeError("Mobile number must be numeric")
			.min(9, "Mobile number must be 9 digits and above")
			.max(15, "Mobile number is too long")
			.required("Mobile number is required"),
	});

	const { mutateAsync: mutateEcocash, isLoading: isLoadingPayment } =
		useEcocashPayBooks(setPaymentIsSuccessful);

	const handleSubmit = async (data) => {
		await mutateEcocash(data);
	};

	// Format phone number as user types
	const formatPhoneInput = (value, setFieldValue) => {
		// Remove non-numeric characters except leading 0
		let cleaned = value.replace(/[^\d]/g, "");
		// Ensure it starts with 0
		if (cleaned.length > 0 && cleaned[0] !== "0") {
			cleaned = "0" + cleaned;
		}
		// Limit to 10 digits
		cleaned = cleaned.slice(0, 10);
		setFieldValue("mobile_number", cleaned);
	};

	return (
		<div className="relative">
			{/* Payment Header with EcoCash USD branding */}
			<div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
				<div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center relative">
					<FaGlobe className="w-5 h-5 text-green-600" />
					<div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-akgreen flex items-center justify-center">
						<FiDollarSign className="w-2.5 h-2.5 text-white" />
					</div>
				</div>
				<div>
					<p className="font-semibold text-gray-900">EcoCash USD Payment</p>
					<p className="text-sm text-gray-500">
						Amount: <span className="font-bold text-akgreener">${grandTotal.toFixed(2)}</span>
					</p>
				</div>
			</div>

			{/* Instructions */}
			<div className="bg-akgreen/5 border border-akgreen/20 rounded-lg p-3 mb-4">
				<div className="flex items-start gap-2">
					<FiSmartphone className="w-5 h-5 text-akgreen flex-shrink-0 mt-0.5" />
					<div className="text-sm text-gray-700">
						<p className="font-medium mb-1">How it works:</p>
						<ol className="list-decimal list-inside space-y-1 text-gray-600">
							<li>Enter your EcoCash registered mobile number</li>
							<li>You'll receive a payment prompt on your phone</li>
							<li>Enter your EcoCash PIN to confirm</li>
						</ol>
					</div>
				</div>
			</div>

			{/* USD Notice */}
			<div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4">
				<div className="flex items-center gap-2 text-sm text-amber-700">
					<FiDollarSign className="w-4 h-4 flex-shrink-0" />
					<span>
						This will be charged to your <strong>USD EcoCash wallet</strong>
					</span>
				</div>
			</div>

			<Formik
				initialValues={initialValues}
				validationSchema={validateCheckout}
				onSubmit={(data) => handleSubmit(data)}
				enableReinitialize={true}
			>
				{(formik) => {
					return (
						<Form id="checkoutFormUsd">
							{/* Phone Number Input */}
							<div className="space-y-2">
								<label className="block text-sm font-medium text-gray-700">
									EcoCash Mobile Number
								</label>
								<div className="relative">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<FiSmartphone className="h-5 w-5 text-gray-400" />
									</div>
									<input
										type="tel"
										name="mobile_number"
										value={formik.values.mobile_number}
										onChange={(e) => formatPhoneInput(e.target.value, formik.setFieldValue)}
										onBlur={formik.handleBlur}
										placeholder="0783 575 000"
										className={`w-full pl-10 pr-4 py-3 border rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-akgreen/20 focus:border-akgreen transition-all ${
											formik.errors.mobile_number && formik.touched.mobile_number
												? "border-red-300 bg-red-50"
												: "border-gray-200"
										}`}
									/>
								</div>
								{formik.errors.mobile_number && formik.touched.mobile_number ? (
									<div className="flex items-center gap-1 text-red-500 text-sm">
										<FiAlertCircle className="w-4 h-4" />
										{formik.errors.mobile_number}
									</div>
								) : (
									<p className="text-xs text-gray-500">
										Enter your 10-digit EcoCash number (e.g., 0783575000)
									</p>
								)}
							</div>

							{/* Submit Button */}
							<div className="mt-5">
								<button
									type="submit"
									disabled={isLoadingPayment || !formik.isValid}
									className="w-full bg-gradient-to-r from-akgreen to-akgreener text-white font-semibold py-3.5 px-6 rounded-xl hover:from-akgreener hover:to-akgreenest transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
								>
									{isLoadingPayment ? (
										<>
											<ButtonLoader size={20} />
											Initiating payment...
										</>
									) : (
										<>
											<FiLock className="w-4 h-4" />
											Pay ${grandTotal.toFixed(2)} USD
										</>
									)}
								</button>
							</div>

							{/* Security Note */}
							<p className="mt-3 text-center text-xs text-gray-500 flex items-center justify-center gap-1">
								<FiLock className="w-3 h-3" />
								Secure payment powered by EcoCash
							</p>
						</Form>
					);
				}}
			</Formik>
		</div>
	);
};

export default EcocashPaymentUsd;
